import { getCountryCodeFromPath } from "@/lib/utils/region"
import { ExternalLinkNotice } from "@/components/external-link-notice"
import { KitchenHoursNotice } from "@/components/kitchen-hours-notice"
import {
  BAKERY_PHONE_TEL,
  GOOGLE_MAPS_DIRECTIONS_URL,
  INSTAGRAM_URL,
  PREORDER_ENABLED,
  TOAST_ORDER_URL,
} from "@/lib/constants/site"
import { useCateringVisible } from "@/lib/hooks/use-site-settings"
import { openPrivacyChoices } from "@/lib/utils/consent"
import { Link, useLocation } from "@tanstack/react-router"

const CTA_HIDDEN_ROUTES = new Set([
  "about",
  "account",
  "cart",
  "checkout",
  "contact",
  "order",
  "visit",
])

const Footer = () => {
  // Catering is a runtime toggle (Admin → Settings → Store).
  const cateringVisible = useCateringVisible()
  const location = useLocation()
  const countryCode = getCountryCodeFromPath(location.pathname) || "us"
  const pageSegment =
    location.pathname.split("/").filter(Boolean).find((segment) => segment !== countryCode) || ""
  const showCta = !CTA_HIDDEN_ROUTES.has(pageSegment)

  return (
    <footer className="bg-[#5F5156] text-[#FFF9F8]">
      {/* CTA Banner */}
      {showCta && (
        <div className="bg-[#F5928A] py-12 px-5 text-center text-[#2B2528] sm:px-6">
          <p className="text-xs tracking-[0.16em] sm:tracking-[0.3em] uppercase font-medium mb-3">
            Rancho Cucamonga, CA
          </p>
          <h2 className="font-display text-3xl md:text-4xl mb-6">
            Come visit us in person
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={GOOGLE_MAPS_DIRECTIONS_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full max-w-sm items-center justify-center rounded-full px-5 sm:px-7 py-3 bg-white text-[#5F5156] text-sm font-medium leading-snug tracking-[0.14em] sm:tracking-widest uppercase transition-all duration-200 hover:bg-[#FFF9F8] active:scale-[0.98] sm:w-auto"
            >
              Get Directions
              <ExternalLinkNotice />
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full max-w-sm items-center justify-center rounded-full px-5 sm:px-7 py-3 border border-[#2B2528] text-[#2B2528] text-sm font-medium leading-snug tracking-[0.14em] sm:tracking-widest uppercase transition-all duration-200 hover:bg-[#2B2528]/10 active:scale-[0.98] sm:w-auto"
            >
              @tusplusbake
              <ExternalLinkNotice />
            </a>
          </div>
        </div>
      )}

      {/* Main Footer */}
      <div className="content-container py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1 flex flex-col gap-5">
            <Link to="/$countryCode" params={{ countryCode }}>
              <span className="inline-flex">
                <img
                  src="/images/plusbake/footer-logo-white.svg"
                  alt="+Bake Cafe"
                  className="h-12 w-auto"
                  width={545}
                  height={189}
                  loading="lazy"
                  decoding="async"
                />
              </span>
            </Link>
            <p className="text-[#FFF9F8] text-sm leading-relaxed font-body">
              Asian-American artisan bakery blending Filipino & Southeast Asian
              flavors with Western baking craft.
            </p>
            <p className="text-[#FFF9F8] text-sm italic font-serif">
              "Taste, See, Enjoy!"
            </p>
          </div>

          {/* Order */}
          <div className="flex flex-col gap-4">
            <h2 className="text-xs tracking-[0.25em] uppercase text-[#FCEAE7] font-medium">
              Order
            </h2>
            <ul className="space-y-3">
              <li>
                <a
                  href={TOAST_ORDER_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#FFF9F8] hover:text-white transition-colors text-sm font-body"
                >
                  Order on Toast
                  <ExternalLinkNotice />
                </a>
              </li>
              {[
                ...(PREORDER_ENABLED
                  ? [{ label: "Preorder Bakery", to: "/$countryCode/store" as const }]
                  : []),
                ...(cateringVisible
                  ? [{ label: "Catering", to: "/$countryCode/catering" as const }]
                  : []),
                { label: "Full Menu", to: "/$countryCode/menu" as const },
                { label: "Our Coffee", to: "/$countryCode/coffee" as const },
                { label: "Salt Bread", to: "/$countryCode/salt-bread" as const },
                { label: "Breakfast & Brunch", to: "/$countryCode/brunch" as const },
                { label: "Matcha", to: "/$countryCode/matcha" as const },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    params={{ countryCode }}
                    className="text-[#FFF9F8] hover:text-white transition-colors text-sm font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Info */}
          <div className="flex flex-col gap-4">
            <h2 className="text-xs tracking-[0.25em] uppercase text-[#FCEAE7] font-medium">
              Info
            </h2>
            <ul className="space-y-3">
              {[
                { label: "About Us", to: "/$countryCode/about" as const },
                { label: "FAQ", to: "/$countryCode/faq" as const },
                { label: "Collaborate", to: "/$countryCode/colab" as const },
                { label: "Contact", to: "/$countryCode/contact" as const },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    params={{ countryCode }}
                    className="text-[#FFF9F8] hover:text-white transition-colors text-sm font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Hours & Location */}
          <div className="flex flex-col gap-4">
            <h2 className="text-xs tracking-[0.25em] uppercase text-[#FCEAE7] font-medium">
              Visit Us
            </h2>
            <div className="space-y-2 text-sm font-body">
              <p className="text-[#FFF9F8] leading-relaxed">
                8685 Base Line Rd, Suite B<br />
                Rancho Cucamonga, CA 91730
              </p>
              <p className="text-[#FFF9F8]">
                Daily 7:30 AM – 3:30 PM
              </p>
              <KitchenHoursNotice tone="dark" className="mt-1" />
              <a
                href={`tel:${BAKERY_PHONE_TEL}`}
                className="text-[#FFF9F8] hover:text-white transition-colors block"
              >
                (909) 256-4016
              </a>
              <a
                href="mailto:talktome@takeurseat.com"
                className="text-[#FFF9F8] hover:text-white transition-colors block break-all"
              >
                talktome@takeurseat.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#FFF9F8]/10 py-6">
        <div className="content-container flex flex-col md:flex-row justify-between items-center gap-3">
          <span className="text-xs text-[#FFF9F8] font-body">
            © {new Date().getFullYear()} +Bake Cafe by Take Ur Seat. All rights reserved.
          </span>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[#FFF9F8] hover:text-white transition-colors font-body"
            >
              Instagram
              <ExternalLinkNotice />
            </a>
            <Link
              to="/$countryCode/privacy-policy"
              params={{ countryCode }}
              className="text-xs text-[#FFF9F8] hover:text-white transition-colors font-body"
            >
              Privacy Policy
            </Link>
            <Link
              to="/$countryCode/terms-and-conditions"
              params={{ countryCode }}
              className="text-xs text-[#FFF9F8] hover:text-white transition-colors font-body"
            >
              Terms
            </Link>
            <Link
              to="/$countryCode/accessibility"
              params={{ countryCode }}
              className="text-xs text-[#FFF9F8] hover:text-white transition-colors font-body"
            >
              Accessibility
            </Link>
            <button
              type="button"
              onClick={openPrivacyChoices}
              className="text-xs text-[#FFF9F8] hover:text-white transition-colors font-body"
            >
              Your Privacy Choices
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
